import {
  IDENTIFICATION_STATUS,
  IDENTIFICATION_STATUS_LABELS,
  identificationStatusOf,
} from 'admin/constants/identificationStatus';
import { formatDate, formatUUID } from 'admin/utils';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { updateAccountAttribute } from '~/javascripts/admin/actions/account';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { colors } from '~/javascripts/admin/theme';
import { createBadge } from '../../../atoms/Badge';
import Input from '../../../atoms/Input';
import Table from '../../../atoms/Table';
import EditableTableCell from '../../../molecules/EditableTableCell';
import SortableTableHeader from '../SortableTableHeader';

type Customer = {
  id: string;
  name: string;
  tel: string;
  email: string;
  money_balance: number;
  point_balance: number;
  registered_at: string;
  last_transaction_done_at: string;
  external_id: string;
  account_status: string;
  account_uuid: string;
  individual_number_identification?: {
    is_identified: boolean;
    last_identified_at: string | null;
    expires_at: string | null;
    // 3値: true=住民 / false=非住民と判定済み / null=まだ判定していない
    is_resident: boolean | null;
  };
};

type Header = {
  label: React.ReactNode;
  size: number;
  align?: 'center' | 'justify' | 'left' | 'right';
};

type Props = {
  customers?: Customer[];
  onChangeSorting?: ({
    sortBy,
    sortDirection,
  }: {
    sortBy: (typeof SORT_BY_OPTIONS)[number];
    sortDirection: (typeof SORT_DIRECTION_OPTIONS)[number];
  }) => void;
  sortOptions: {
    sortBy: (typeof SORT_BY_OPTIONS)[number];
    sortDirection: (typeof SORT_DIRECTION_OPTIONS)[number];
  };
};

const SORT_BY_OPTIONS = ['last-transaction-done-at', 'registered-at'] as const;
const SORT_DIRECTION_OPTIONS = ['asc', 'desc'] as const;

const TooltipTableCell = styled.div<{ tooltip?: boolean }>`
  &:hover {
    ${({ tooltip }) => tooltip && 'cursor: pointer; background-color: #eeeeee;'}
  }

  padding: 4px 0;
`;

const AccountStatusBadge = createBadge({
  active: {
    label: '有効',
    color: colors.white,
    bgColor: colors.green['300'],
  },
  suspended: {
    label: '凍結中',
    color: colors.gray['300'],
    bgColor: colors.white,
    borderColor: colors.gray['300'],
  },
  'pre-closed': {
    label: '退会準備中',
    color: colors.gray['900'],
    bgColor: colors.gray['200'],
  },
  closed: {
    label: '退会済',
    color: colors.white,
    bgColor: colors.gray['800'],
  },
});

// Keys match IdentificationStatus so identificationStatusOf's result can be passed straight through
const IdentificationBadge = createBadge({
  [IDENTIFICATION_STATUS.VERIFIED]: {
    label: IDENTIFICATION_STATUS_LABELS[IDENTIFICATION_STATUS.VERIFIED],
    color: '#1e6acc',
    borderColor: '#1e6acc',
    bgColor: 'transparent',
  },
  [IDENTIFICATION_STATUS.IS_RESIDENT]: {
    label: IDENTIFICATION_STATUS_LABELS[IDENTIFICATION_STATUS.IS_RESIDENT],
    color: '#1eac21',
    borderColor: '#1eac21',
    bgColor: 'transparent',
  },
});

const CustomerListTable = ({ customers = [], sortOptions, onChangeSorting }: Props) => {
  const [customerExternalIds, setCustomerExernalIds] = useState<Record<string, string | undefined>>(
    {}
  );
  const [editableRows, setEditableRows] = useState<Record<string, boolean>>({});
  const dispatch = useAppDispatch();

  const handleUpdate = (rowId: string, updatedValue?: string) => {
    updateAccountAttribute(rowId, { external_id: updatedValue }).catch((errorRes) => {
      errorRes.then(console.error);

      const options = {
        body: 'エラーが発生しました。管理者へお問合せください',
        confirmText: 'OK',
      };
      dispatch(openAlertModal(options));
      setCustomerExernalIds((prev) => ({
        ...prev,
        [rowId]: customers.find((c) => c.account_uuid === rowId)?.external_id,
      }));
    });
  };

  const handleCancel = (rowId: string) => {
    setCustomerExernalIds((prev) => ({ ...prev, [rowId]: undefined }));
  };

  const handleIsEditableToggle = (rowId: string, editMode: boolean) => {
    setEditableRows((prevEditableRows) => ({
      ...prevEditableRows,
      [rowId]: editMode,
    }));
  };

  const handleToggleSort = (key: (typeof SORT_BY_OPTIONS)[number]) => {
    if (sortOptions.sortBy !== key) {
      onChangeSorting?.({
        sortBy: key,
        sortDirection: 'desc',
      });
      return;
    }

    onChangeSorting?.({
      ...sortOptions,
      sortDirection: sortOptions.sortDirection === 'desc' ? 'asc' : 'desc',
    });
  };

  const HEADERS: Header[] = [
    { label: '状態', size: 0.5 },
    {
      label: (
        <TooltipTableCell tooltip data-tip data-for="customerId">
          お客様ID
          <ReactTooltip id="customerId" type="dark" place="top" effect="solid">
            <span>お客様ごとに自動採番されるID*先頭8桁を表示</span>
          </ReactTooltip>
        </TooltipTableCell>
      ),
      size: 1,
    },
    { label: 'マイナンバー', size: 1 },
    { label: 'ニックネーム', size: 1.5 },
    { label: '携帯番号', size: 1 },
    { label: 'メールアドレス', size: 1.5 },
    { label: 'マネー', size: 1, align: 'right' },
    { label: 'ポイント', size: 1, align: 'right' },
    {
      label: (
        <SortableTableHeader
          label="会員登録日時"
          onClick={() => handleToggleSort('registered-at')}
          direction={sortOptions.sortBy === 'registered-at' ? sortOptions.sortDirection : 'neutral'}
        />
      ),
      size: 1.5,
    },
    {
      label: (
        <SortableTableHeader
          label="最終取引日時"
          onClick={() => handleToggleSort('last-transaction-done-at')}
          direction={
            sortOptions.sortBy === 'last-transaction-done-at'
              ? sortOptions.sortDirection
              : 'neutral'
          }
        />
      ),
      size: 1.5,
    },
    { label: '外部ID', size: 1.5 },
  ];

  return (
    <Table styles={{ marginTop: '20px', marginBottom: '20px' }}>
      <Table.Header>
        <Table.Row>
          {HEADERS.map((h) => (
            <Table.Cell size={h.size} align={h.align}>
              {h.label}
            </Table.Cell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {customers?.map((row) => {
          const moneyBalance = row.money_balance?.toLocaleString();
          const pointBalance = row.point_balance?.toLocaleString();
          const identificationStatus = identificationStatusOf(row.individual_number_identification);

          return (
            <Table.Row key={row.id}>
              <Table.Cell wordBreak="keep-all">
                <AccountStatusBadge status={row.account_status} />
              </Table.Cell>
              <Table.Cell wordBreak="keep-all">
                <Link to={`/customers/${row.id}`}>{formatUUID(row.id)}</Link>
              </Table.Cell>
              <Table.Cell wordBreak="keep-all">
                {identificationStatus !== IDENTIFICATION_STATUS.NOT_VERIFIED && (
                  <IdentificationBadge status={identificationStatus} />
                )}
              </Table.Cell>
              <Table.Cell ellipsis>
                <span title={row.name}>{row.name}</span>
              </Table.Cell>
              <Table.Cell ellipsis>
                <span title={row.tel}>{row.tel}</span>
              </Table.Cell>
              <Table.Cell ellipsis>
                <span title={row.email}>{row.email}</span>
              </Table.Cell>
              <Table.Cell wordBreak="keep-all" align="right">
                <span title={moneyBalance}>{moneyBalance}</span>
              </Table.Cell>
              <Table.Cell wordBreak="keep-all" align="right">
                <span title={pointBalance}>{pointBalance}</span>
              </Table.Cell>
              <Table.Cell>{formatDate(row.registered_at)}</Table.Cell>
              <Table.Cell>{formatDate(row.last_transaction_done_at)}</Table.Cell>
              <Table.Cell>
                <EditableTableCell
                  isEditable={editableRows[row.account_uuid] || false}
                  onToggle={(editableState) =>
                    handleIsEditableToggle(row.account_uuid, editableState)
                  }
                  onUpdate={() =>
                    handleUpdate(row.account_uuid, customerExternalIds[row.account_uuid])
                  }
                  onCancel={() => handleCancel(row.account_uuid)}
                  renderInput={() => (
                    <Input
                      key={row.account_uuid}
                      type="text"
                      syncValue={customerExternalIds[row.account_uuid] ?? row.external_id ?? ''}
                      onChange={({ target: { value } }) => {
                        setCustomerExernalIds((prevInput) => ({
                          ...prevInput,
                          [row.account_uuid]: value,
                        }));
                      }}
                      inputStyles={{
                        width: '100%',
                      }}
                      maxLength={50}
                    />
                  )}
                  renderValue={() => (
                    <div style={{ width: '75%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      <span>{customerExternalIds[row.account_uuid] ?? row.external_id ?? ''}</span>
                    </div>
                  )}
                />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default CustomerListTable;
